import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["closeable", "inline", "multiline", "use", "_flexWrapperStyles", "_avatar", "href", "target", "onClick", "onCloseClick", "id", "role", "aria-label", "onMouseDown", "onTouchEnd", "truncateStringProps", "children"];
import { useRef } from 'react';
import styled from 'foundations-theming/utils';
import { uniqueId } from '../../utils/uniqueId';
import { CloseButton } from '../CloseButton/CloseButton';
import { Clickable } from '../Clickable/Clickable';
import { Link } from '../Link/Link';
import { TruncateString } from '../TruncateString/TruncateString';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { filterProps } from '../../../utils/filterProps';
import { TagProvider } from './TagProvider';
import { useStatusTagContext } from '../StatusTag/StatusTagProvider';
import { deepMerge } from '../../utils/deepMerge/deepMerge';
import { colorFamilyToTokenBackgroundColor, colorFamilyToTokenBorderColor } from '../../utils/color/legacyColorFamily';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const backgroundColorMapping = Object.assign({}, colorFamilyToTokenBackgroundColor, {
  /**
   * Custom
   */
  default: 'fill-field-default'
});
const borderColorMapping = Object.assign({}, colorFamilyToTokenBorderColor, {
  /**
   * Custom
   */
  default: 'border-core-hover'
});
const STATUS_ALIAS_MAP = {
  disabled: 'default',
  paused: 'default',
  draft: 'default',
  primary: 'default',
  active: 'success',
  sent: 'success',
  published: 'success',
  inReview: 'warning',
  scheduled: 'warning',
  removed: 'danger',
  cancelled: 'danger',
  error: 'danger'
};
export const USE_COLOR_ALIAS_MAP = {
  // defaults
  disabled: 'default',
  paused: 'default',
  draft: 'default',
  primary: 'default',
  // oz
  success: 'oz',
  active: 'oz',
  sent: 'oz',
  published: 'oz',
  // marigold
  warning: 'marigold',
  inReview: 'marigold',
  scheduled: 'marigold',
  // cand-apple
  danger: 'candy-apple',
  removed: 'candy-apple',
  cancelled: 'candy-apple',
  error: 'candy-apple',
  // calypso
  info: 'calypso'
};
export const StyledTag = styled.span.withConfig({
  displayName: "Tag__StyledTag",
  componentId: "fos5ah-0"
})(({
  $computedUse,
  $closeable,
  $inline,
  $cssStyles,
  theme,
  $styleOverrides
}) => {
  var _backgroundColorMappi, _borderColorMapping;
  const tokenizedBackgroundColor = (_backgroundColorMappi = backgroundColorMapping[$computedUse]) !== null && _backgroundColorMappi !== void 0 ? _backgroundColorMappi : 'icon-core-default';
  const tokenizedBorderColor = (_borderColorMapping = borderColorMapping[$computedUse]) !== null && _borderColorMapping !== void 0 ? _borderColorMapping : 'fill-transparent-default';
  return deepMerge(Object.assign({
    fontFamily: theme.typography['heading-25'].fontFamily,
    fontSize: theme.typography['heading-25'].fontSize,
    backgroundColor: theme.color[tokenizedBackgroundColor],
    borderBlockWidth: theme.borderWidth['100'],
    borderInlineWidth: theme.borderWidth['100'],
    borderBlockStyle: 'solid',
    borderInlineStyle: 'solid',
    borderBlockColor: theme.color[tokenizedBorderColor],
    borderInlineColor: theme.color[tokenizedBorderColor],
    borderStartStartRadius: theme.borderRadius['transitional-full-0'],
    borderStartEndRadius: theme.borderRadius['transitional-full-0'],
    borderEndEndRadius: theme.borderRadius['transitional-full-0'],
    borderEndStartRadius: theme.borderRadius['transitional-full-0'],
    color: $computedUse === 'olaf' ? theme.color['text-core-on-fill-default'] : theme.color['text-core-default'],
    lineHeight: '22px',
    paddingBlock: theme.space['0'],
    paddingInline: theme.space['200'],
    position: 'relative',
    cursor: 'inherit',
    display: 'inline-flex',
    fontWeight: theme.typography['heading-25'].fontWeight,
    maxInlineSize: '100%',
    overflow: 'hidden',
    userSelect: 'none',
    verticalAlign: $inline ? 'middle' : theme.space['200'],
    paddingInlineEnd: $closeable ? theme.space['700'] : undefined,
    marginBlock: $inline ? theme.space['0'] : undefined,
    marginInline: $inline ? theme.space['100'] : undefined,
    /* Applies spacing between inner elements (ex. text and icon) */
    '& > * + *': {
      marginInlineStart: theme.space['200']
    },
    /* Applies spacing between sibling tags */
    '& + &': {
      marginInlineStart: $inline ? undefined : theme.space['300']
    },
    /* Don't color links in a tag */
    '& a, & a:hover, & a:active': {
      color: 'inherit'
    }
  }, $cssStyles), $styleOverrides);
});
const FlexWrapper = styled.span.withConfig({
  displayName: "Tag__FlexWrapper",
  componentId: "fos5ah-1"
})(({
  $cssStyles,
  $styleOverrides
}) => {
  return deepMerge(Object.assign({
    display: 'inline-flex',
    maxInlineSize: '100%',
    verticalAlign: 'top'
  }, $cssStyles), $styleOverrides);
});
const ContentWrapper = styled.span.withConfig({
  displayName: "Tag__ContentWrapper",
  componentId: "fos5ah-2"
})(() => {
  return {
    overflow: 'hidden'
  };
});
const closeButtonStyleOverrides = {
  insetBlockStart: '0',
  insetBlockEnd: '0',
  insetInlineEnd: '6px',
  color: 'currentColor',
  marginBlockStart: 'auto',
  marginBlockEnd: 'auto',
  position: 'absolute',
  transform: 'scale(0.5)'
};
const linkStyleOverrides = {
  overflow: 'hidden'
};
const StyledClickable = styled(Clickable)(({
  theme
}) => {
  return {
    '&&': {
      fontWeight: theme.typography['heading-200'].fontWeight,
      overflow: 'hidden',
      textDecoration: 'underline'
    }
  };
});
const renderContents = (href, target, onClick, truncatedChildren) => {
  if (href) {
    return /*#__PURE__*/_jsx(Link, {
      href: href,
      onClick: onClick,
      target: target,
      use: "on-bright",
      children: truncatedChildren
    });
  } else if (onClick) {
    return /*#__PURE__*/_jsx(StyledClickable, {
      onClick: onClick,
      children: truncatedChildren
    });
  }
  return /*#__PURE__*/_jsx(ContentWrapper, {
    children: truncatedChildren
  });
};
export const TAG_COMPONENT_NAME = 'UITag';
export const Tag = _ref => {
  var _statusTagContext$tag, _statusTagContext$tag2;
  let {
      closeable = false,
      inline = false,
      multiline = false,
      use = 'default',
      _flexWrapperStyles = {},
      _avatar,
      href,
      target,
      onClick,
      onCloseClick,
      id,
      role,
      'aria-label': ariaLabel,
      onMouseDown,
      onTouchEnd,
      truncateStringProps,
      children
    } = _ref,
    restProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    domProps,
    tokenProps
  } = filterProps(restProps);
  const containerCssStyles = useMapTokenPropsToCSS(tokenProps);
  const flexWrapperCssStyles = useMapTokenPropsToCSS(_flexWrapperStyles);
  const statusTagContext = useStatusTagContext();
  const idRef = useRef(uniqueId('tag-'));
  const computedId = id || idRef.current;
  const truncatedChildren = multiline ? children : /*#__PURE__*/_jsx(TruncateString, Object.assign({
    useFlex: true
  }, truncateStringProps, {
    children: children
  }));
  return /*#__PURE__*/_jsx(StyledTag
  /**
   * "UITag" is intentional to provide backwards compatibility with Selenium helpers
   * @see https://git.hubteam.com/HubSpot/foundations-components/issues/5187
   * @see https://git.hubteam.com/HubSpot/hs-selenium/blob/master/selenium-js-utils/src/UIComponents/select/internal/core.ts#L25
   */, Object.assign({
    "data-component-name": TAG_COMPONENT_NAME,
    id: computedId,
    role: role,
    "aria-label": ariaLabel,
    onMouseDown: onMouseDown,
    onTouchEnd: onTouchEnd,
    $computedUse: USE_COLOR_ALIAS_MAP[use] || use,
    $closeable: closeable,
    $inline: inline,
    $cssStyles: containerCssStyles,
    $styleOverrides: (_statusTagContext$tag = statusTagContext === null || statusTagContext === void 0 ? void 0 : statusTagContext.tagStyleOverrides) !== null && _statusTagContext$tag !== void 0 ? _statusTagContext$tag : {}
  }, domProps, {
    children: /*#__PURE__*/_jsxs(TagProvider, {
      closeButtonStyleOverrides: closeButtonStyleOverrides,
      linkStyleOverrides: linkStyleOverrides,
      children: [/*#__PURE__*/_jsxs(FlexWrapper, {
        $cssStyles: flexWrapperCssStyles,
        $styleOverrides: (_statusTagContext$tag2 = statusTagContext === null || statusTagContext === void 0 ? void 0 : statusTagContext.tagFlexWrapperStyleOverrides) !== null && _statusTagContext$tag2 !== void 0 ? _statusTagContext$tag2 : {},
        children: [_avatar, renderContents(href, target, onClick, truncatedChildren)]
      }), closeable && /*#__PURE__*/_jsx(CloseButton, {
        "aria-describedby": computedId,
        onClick: onCloseClick
      })]
    })
  }));
};